// ADR-0006: trilingual runtime. `t(key, params?)` is exported as a
// context-free function (for tests) and is also exposed through the
// `useI18n()` React hook. The active locale is stored in a module-level
// signal so non-React call sites (audio cues, push notifications) can use it
// without a provider.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import { DEFAULT_LOCALE, LOCALES, type Locale } from "../types/i18n";
import en from "./en.json";
import de from "./de.json";
import hu from "./hu.json";
type Catalogue = Record<string, unknown>;

const CATALOGUES: Record<Locale, Catalogue> = { en, de, hu };

/** The in-process active locale. The provider writes to it on every change. */
let _active: Locale = DEFAULT_LOCALE;
const _listeners = new Set<(l: Locale) => void>();

export function getActiveLocale(): Locale {
  return _active;
}

export function setLocale(l: Locale): void {
  if (!LOCALES.includes(l)) return;
  if (_active === l) return;
  _active = l;
  for (const fn of _listeners) fn(l);
}

export function onLocaleChange(fn: (l: Locale) => void): () => void {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
}

function getByPath(obj: unknown, dotted: string): unknown {
  return dotted.split(".").reduce<unknown>((acc, part) => {
    if (acc && typeof acc === "object") {
      return (acc as Record<string, unknown>)[part];
    }
    return undefined;
  }, obj);
}

function interpolate(template: string, params?: Record<string, string | number>): string {
  if (!params) return template;
  return template.replace(/\{\{(\w+)\}\}/g, (_m, name: string) => {
    const v = params[name];
    return v === undefined ? `{{${name}}}` : String(v);
  });
}

const warned = new Set<string>();

/**
 * Look up a dotted key in the requested locale, falling back to English, then
 * to the key itself rendered in a visible marker in dev. Production never
 * renders an empty string or `undefined`.
 */
export function t(
  key: string,
  params?: Record<string, string | number>,
  locale: Locale = _active
): string {
  const primary = getByPath(CATALOGUES[locale], key);
  if (typeof primary === "string") return interpolate(primary, params);
  const fallback = getByPath(CATALOGUES.en, key);
  if (typeof fallback === "string") return interpolate(fallback, params);
  if (import.meta.env?.DEV) {
    const warnKey = `${locale}:${key}`;
    if (!warned.has(warnKey)) {
      warned.add(warnKey);
      // eslint-disable-next-line no-console
      console.warn(`[i18n] missing key: ${warnKey}`);
    }
    return `⟨${key}⟩`;
  }
  return key;
}

// --- React context ---------------------------------------------------------

interface I18nContextValue {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({
  children,
  initial,
}: {
  children: ReactNode;
  initial?: Locale;
}): ReactElement {
  const [locale, setLocaleState] = useState<Locale>(initial ?? _active);

  useLayoutEffect(() => {
    if (initial) setLocale(initial);
  }, [initial]);

  useLayoutEffect(() => {
    // Two-way sync: external callers (e.g. route layouts) can set the
    // module-level locale directly, and the provider state follows.
    return onLocaleChange((l) => setLocaleState(l));
  }, []);

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = locale;
    }
  }, [locale]);

  const setLocaleFn = useCallback((l: Locale) => setLocale(l), []);
  const tFn = useCallback(
    (key: string, params?: Record<string, string | number>) => t(key, params, locale),
    [locale]
  );

  const value = useMemo<I18nContextValue>(
    () => ({ locale, setLocale: setLocaleFn, t: tFn }),
    [locale, setLocaleFn, tFn]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within <I18nProvider>");
  return ctx;
}
